import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from 'baseui/spinner';
import { SIZE } from 'baseui/button';
import apiClient from './shared/hooks/AuthService';
import AiResponseRenderer from './AiResponseRenderer';
import { AI_DAILY_LIMITS } from '../config/aiLimits';
import { FEATURE } from '../config/planFeatures';
import { useUserPlan } from '../hooks/useUserPlan';
import PlanTierLabel from './billing/PlanTierLabel';
import './ExplainSection.css';

const AI_REQUEST_TIMEOUT_MS = 180000;
const explainCache = new Map();

const EXPLAIN_KEYS = [
  'explanation',
  'content',
  'text',
  'result',
  'response',
  'message',
  'body',
];

const MEMORY_MAP_KEYS = [
  'improvedAnswer',
  'suggestions',
  'content',
  'text',
  'result',
  'response',
  'message',
  'answer',
  'body',
];

const RELATED_TOPIC_KEYS = [
  'relatedTopics',
  'content',
  'text',
  'result',
  'response',
  'message',
  'body',
];

const isDailyAiLimitMessage = (text) =>
  typeof text === 'string' &&
  (/daily ai limit reached/i.test(text) || /limit reached for today/i.test(text));

const isProFeatureMessage = (text) =>
  typeof text === 'string' && /pro feature|upgrade in settings/i.test(text);

const extractAiText = (data, keys) => {
  if (data == null) return null;
  if (typeof data === 'string') {
    const trimmed = data.trim();
    return trimmed || null;
  }
  if (typeof data !== 'object') return null;

  if (data.error && typeof data.error === 'string') {
    return null;
  }

  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  if (data.data && typeof data.data === 'object') {
    return extractAiText(data.data, keys);
  }

  return null;
};

const ExplainSection = ({ questionData }) => {
  const { canUseFeature, planReady, plan } = useUserPlan();
  const cacheKey = questionData?.id ?? 'unknown';
  const mountedRef = useRef(true);

  const [activeView, setActiveView] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);
  const [explanation, setExplanation] = useState(
    () => explainCache.get(`${cacheKey}:explanation:v3`) || null
  );
  const [memoryMap, setMemoryMap] = useState(
    () => explainCache.get(`${cacheKey}:memory:v3`) || null
  );
  const [relatedTopics, setRelatedTopics] = useState(
    () => explainCache.get(`${cacheKey}:topics:v1`) || null
  );
  const [aiUsage, setAiUsage] = useState(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/api/users/me/usage')
      .then((response) => {
        if (!cancelled && response.data) {
          setAiUsage((prev) => ({ ...prev, ...response.data }));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cacheKey]);

  if (!questionData) return null;

  const questionText =
    (questionData.title || questionData.question || '').trim();

  const buildPayload = () => ({
    id: questionData.id,
    title: questionText,
    question: questionText,
    description: questionData.answer,
    topic: questionData.topic,
    category: questionData.category,
    subCategory1: questionData.subCategory1,
    subCategory2: questionData.subCategory2,
    answer: questionData.answer,
    extraInfo: questionData.extraInfo,
  });

  const resolveErrorMessage = (error) => {
    if (error.code === 'ECONNABORTED') {
      return 'The request timed out. The AI service may be slow—please try again.';
    }
    if (error.response?.status === 403) {
      return (
        error.response?.data?.error ||
        'Mind map AI search is a Pro feature. Upgrade in Settings → Billing.'
      );
    }
    if (error.response?.status === 429) {
      return (
        error.response?.data?.error ||
        'Daily AI limit reached. Try again tomorrow or upgrade for a higher cap.'
      );
    }
    return (
      error.response?.data?.error ||
      error.message ||
      'Unable to reach the AI service right now. Please try again.'
    );
  };

  const mindMapProLocked = planReady ? !canUseFeature(FEATURE.MIND_MAP_AI) : true;

  const BillingUpgradeNote = ({ message }) => (
    <p className="explain-upgrade-note">
      {message}{' '}
      <Link to="/settings?tab=billing" className="explain-upgrade-link">
        Upgrade
      </Link>
    </p>
  );

  const ProUpgradeHint = () => (
    <BillingUpgradeNote message="You need to upgrade to Pro or Team to use mind map AI search and related topics." />
  );

  const DailyLimitUpgradeHint = () => (
    <div className="explain-credits-out">
      <p className="explain-credits-out__title">Today&apos;s AI credits are used up</p>
      <p className="explain-upgrade-note">
        Your daily quota resets tomorrow. Pro includes {AI_DAILY_LIMITS.pro} AI requests/day plus mind map
        search — keep going without waiting.{' '}
        <Link to="/settings?tab=billing" className="explain-upgrade-link">
          Upgrade
        </Link>
      </p>
    </div>
  );

  const renderPanelBody = () => {
    if (mindMapProLocked && (activeView === 'memory' || activeView === 'topics')) {
      return <ProUpgradeHint />;
    }
    if (isLoading) {
      return (
        <div className="explain-loading ai-loading-state">
          <Spinner />
          <span>
            {activeView === 'memory'
              ? 'Building your mind map…'
              : activeView === 'topics'
                ? 'Searching related topics…'
                : 'Generating in-depth explanation…'}
          </span>
        </div>
      );
    }
    if (!activeContent) return null;

    if (isDailyAiLimitMessage(activeContent)) {
      return <DailyLimitUpgradeHint />;
    }
    if (isProFeatureMessage(activeContent)) {
      return <BillingUpgradeNote message={activeContent} />;
    }

    return (
      <AiResponseRenderer
        content={activeContent}
        variant={activeView === 'memory' ? 'memory' : 'explain'}
      />
    );
  };

  const fetchExplanation = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();

    if (
      explanation &&
      !explanation.startsWith('Unable') &&
      !explanation.startsWith('The request') &&
      !explanation.startsWith('No explanation') &&
      !isDailyAiLimitMessage(explanation)
    ) {
      setActiveView('explanation');
      return;
    }

    if (!questionText) {
      setActiveView('explanation');
      setExplanation('This card has no question text to explain.');
      return;
    }

    setActiveView('explanation');
    setLoadingAction('explanation');
    setExplanation(null);

    try {
      const response = await apiClient.post(
        '/api/ai/explain',
        buildPayload(),
        { timeout: AI_REQUEST_TIMEOUT_MS }
      );

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      const text = extractAiText(response.data, EXPLAIN_KEYS);
      if (!text) {
        throw new Error(
          'No explanation was returned. Restart the backend and try again.'
        );
      }

      if (!mountedRef.current) return;
      explainCache.set(`${cacheKey}:explanation:v3`, text);
      setExplanation(text);
      if (response.data?.usage) setAiUsage(response.data.usage);
    } catch (error) {
      console.error('Explain error:', error);
      if (!mountedRef.current) return;
      setExplanation(resolveErrorMessage(error));
    } finally {
      if (mountedRef.current) {
        setLoadingAction(null);
      }
    }
  };

  const fetchMemoryMap = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();

    if (mindMapProLocked) {
      setActiveView('memory');
      return;
    }

    if (memoryMap && !memoryMap.startsWith('Unable') && !memoryMap.startsWith('The request') && !memoryMap.startsWith('No memory')) {
      setActiveView('memory');
      return;
    }

    if (!questionText) {
      setActiveView('memory');
      setMemoryMap('This card has no question text to summarize.');
      return;
    }

    setActiveView('memory');
    setLoadingAction('memory');
    setMemoryMap(null);

    try {
      const response = await apiClient.post(
        '/api/ai/suggest-improvements',
        buildPayload(),
        { timeout: AI_REQUEST_TIMEOUT_MS }
      );

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      const text = extractAiText(response.data, MEMORY_MAP_KEYS);
      if (!text) {
        throw new Error(
          'No memory map was returned. Restart the backend and try again.'
        );
      }

      if (!mountedRef.current) return;
      explainCache.set(`${cacheKey}:memory:v3`, text);
      setMemoryMap(text);
      if (response.data?.usage) setAiUsage(response.data.usage);
    } catch (error) {
      console.error('Memory map error:', error);
      if (!mountedRef.current) return;
      setMemoryMap(resolveErrorMessage(error));
    } finally {
      if (mountedRef.current) {
        setLoadingAction(null);
      }
    }
  };

  const fetchRelatedTopics = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();

    if (mindMapProLocked) {
      setActiveView('topics');
      return;
    }

    if (
      relatedTopics &&
      !relatedTopics.startsWith('Unable') &&
      !relatedTopics.startsWith('The request') &&
      !relatedTopics.startsWith('No related')
    ) {
      setActiveView('topics');
      return;
    }

    if (!questionText) {
      setActiveView('topics');
      setRelatedTopics('This card has no question text to search from.');
      return;
    }

    setActiveView('topics');
    setLoadingAction('topics');
    setRelatedTopics(null);

    try {
      const response = await apiClient.post(
        '/api/ai/related-topics',
        buildPayload(),
        { timeout: AI_REQUEST_TIMEOUT_MS }
      );

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      const text = extractAiText(response.data, RELATED_TOPIC_KEYS);
      if (!text) {
        throw new Error(
          'No related topics were returned. Restart the backend and try again.'
        );
      }

      if (!mountedRef.current) return;
      explainCache.set(`${cacheKey}:topics:v1`, text);
      setRelatedTopics(text);
      if (response.data?.usage) setAiUsage(response.data.usage);
    } catch (error) {
      console.error('Related topics error:', error);
      if (!mountedRef.current) return;
      setRelatedTopics(resolveErrorMessage(error));
    } finally {
      if (mountedRef.current) {
        setLoadingAction(null);
      }
    }
  };

  const activeContent =
    activeView === 'explanation'
      ? explanation
      : activeView === 'memory'
        ? memoryMap
        : activeView === 'topics'
          ? relatedTopics
          : null;

  const isLoading = loadingAction === activeView;
  const showPanel = activeView !== null;

  const used = aiUsage?.aiUsedToday ?? 0;
  const limit = aiUsage?.aiDailyLimit ?? 0;
  const usagePct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const usageFull = aiUsage?.aiRemaining === 0;
  const usageWarn = !usageFull && limit > 0 && used / limit >= 0.8;

  const actionClass = (view, opts = {}) => {
    const parts = ['explain-action'];
    if (opts.pro) parts.push('explain-action--pro');
    if (activeView === view) parts.push('explain-action--active');
    if (view === 'explanation') parts.push('explain-action--primary');
    return parts.join(' ');
  };

  return (
    <section className="explain-deeper" onClick={(e) => e.stopPropagation()}>
      <p className="explain-deeper__eyebrow">
        <span className="explain-deeper__eyebrow-dot" aria-hidden />
        Go deeper
      </p>
      <p className="explain-deeper__lead">
        Layered explanations on any plan. Pro unlocks mind map AI search and related topics
        for quick recall and what to study next.
      </p>

      <div className="explain-deeper__actions">
        <button
          type="button"
          className={actionClass('explanation')}
          onClick={fetchExplanation}
          disabled={loadingAction === 'explanation'}
        >
          {loadingAction === 'explanation' ? (
            <Spinner $size={SIZE.mini} />
          ) : (
            'Explain in depth'
          )}
        </button>
        <button
          type="button"
          className={actionClass('memory', { pro: true })}
          onClick={fetchMemoryMap}
          disabled={loadingAction === 'memory'}
        >
          {loadingAction === 'memory' ? (
            <Spinner $size={SIZE.mini} />
          ) : (
            <>
              Mind map AI search
              {mindMapProLocked && <PlanTierLabel tier="Pro" />}
            </>
          )}
        </button>
        <button
          type="button"
          className={actionClass('topics', { pro: true })}
          onClick={fetchRelatedTopics}
          disabled={loadingAction === 'topics'}
        >
          {loadingAction === 'topics' ? (
            <Spinner $size={SIZE.mini} />
          ) : (
            <>
              Related topics
              {mindMapProLocked && <PlanTierLabel tier="Pro" />}
            </>
          )}
        </button>
      </div>

      {limit > 0 && (
        <div className="explain-usage">
          <div className="explain-usage__row">
            <span>
              AI today: <strong>{used}</strong> / {limit}
            </span>
            {usageFull && <span>Credits exhausted — resets tomorrow</span>}
            {!usageFull && usageWarn && <span>Running low</span>}
          </div>
          <div className="explain-usage__bar" role="progressbar" aria-valuenow={used} aria-valuemin={0} aria-valuemax={limit}>
            <div
              className={`explain-usage__fill${usageFull ? ' explain-usage__fill--full' : usageWarn ? ' explain-usage__fill--warn' : ''}`}
              style={{ width: `${usagePct}%` }}
            />
          </div>
          {usageFull && plan === 'FREE' && (
            <p className="explain-usage__hint">
              AI credits exhausted for today.{' '}
              <Link to="/settings?tab=billing" className="explain-upgrade-link">
                Upgrade
              </Link>{' '}
              for {AI_DAILY_LIMITS.pro}/day and Pro-only mind maps.
            </p>
          )}
        </div>
      )}

      {showPanel && (
        <div className="explain-panel ai-response-content">
          <h3 className="explain-panel__title">
            {activeView === 'explanation'
              ? 'In-depth explanation'
              : activeView === 'topics'
                ? 'Related topics'
                : 'Mind map'}
          </h3>
          {renderPanelBody()}
        </div>
      )}
    </section>
  );
};

export default ExplainSection;
